import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import {
  MatSidenav,
  MatSidenavContainer,
  MatSidenavContent,
} from '@angular/material/sidenav';
import { MatToolbar } from '@angular/material/toolbar';
import { MatTooltip } from '@angular/material/tooltip';
import { Router, RouterLink, RouterOutlet } from '@angular/router';
import { switchMap } from 'rxjs';
import { HasPermissionDirective, UserService, WebSocketService } from '@ipg/ngx-frontend-toolset';
import { User } from '../../../models/user.model';
import { InvoiceSetupService } from '../../../services/invoice-setup.service';
import { PermissionSyncService } from '../../../services/permission-sync.service';
import { UnitService } from '../../../views/units/services/unit.service';
import { HeaderComponent } from '../../header/header.component';
import { SidenavMenuComponent } from '../sidenav-menu/sidenav-menu.component';

@Component({
  selector: 'app-main-layout',
  imports: [
    MatIconButton,
    MatIcon,
    MatToolbar,
    MatTooltip,
    MatSidenav,
    MatSidenavContainer,
    MatSidenavContent,
    MatButton,
    RouterOutlet,
    RouterLink,
    SidenavMenuComponent,
    HeaderComponent,
    HasPermissionDirective,
  ],
  templateUrl: './main-layout.component.html',
  styleUrl: './main-layout.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MainLayoutComponent {
  private readonly _unitService = inject(UnitService);
  private readonly _userService = inject<UserService<User>>(UserService);
  private readonly _router = inject(Router);
  private readonly _wsService = inject(WebSocketService);
  private readonly _permissionSync = inject(PermissionSyncService);
  private readonly _invoiceSetupService = inject(InvoiceSetupService);

  protected readonly currentUnit = this._unitService.currentUnit;

  /**
   * While onboarding is pending the shell renders the wizard only: every other
   * route is blocked by `onboardingGuard`, so leaving the menu in place would
   * just offer links that bounce straight back.
   */
  protected readonly onboardingPending =
    this._invoiceSetupService.onboardingPending;

  /** The user's originally assigned unit ID (stays constant) */
  private readonly _homeUnitId = this._userService.data()?.rootUnitId;

  protected readonly isViewingOtherUnit = computed(() => {
    const userData = this._userService.data();
    return userData ? userData.unitId !== this._homeUnitId : false;
  });

  constructor() {
    this._listenForRoleChanges();
  }

  protected returnToHomeUnit(): void {
    if (!this._homeUnitId) return;

    this._unitService
      .activate(this._homeUnitId)
      .pipe(switchMap(() => this._userService.fetch()))
      .subscribe(() => {
        this._router.navigate(['/clients']);
      });
  }

  private _listenForRoleChanges(): void {
    const userId = this._userService.data()?._id;
    if (!userId) return;

    this._wsService
      .connect<{ userId: string }>('/event-stream', {
        requireAuth: true,
        topics: [`user/${userId}/role-changed`],
      })
      .pipe(takeUntilDestroyed())
      .subscribe({
        next: (connection) => {
          connection.events$.subscribe((event) => {
            if (event.eventType === 'user.moved') {
              this._permissionSync.forceLogout('moved');
            } else if (event.eventType === 'user.support_session.ended') {
              this._permissionSync.forceLogout('support_session_ended');
            } else if (event.eventType === 'user.unit_tree.changed') {
              this._permissionSync.syncPermissions('unit-tree');
            } else if (event.eventType === 'user.role.changed') {
              this._permissionSync.syncPermissions('role');
            }
          });
        },
      });
  }
}

